import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import fetchProfile from "../../api/userProfile";
import { ApiMethod, apiCatalog, apiCategories } from "./apiCatalog";
import "./css/apiDocs.scss";

const BASE_URL = "https://ai.addd.co.kr/api";

const ApiDocs = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("전체");
  const [method, setMethod] = useState<"전체" | ApiMethod>("전체");
  const [expandedId, setExpandedId] = useState<string | null>(apiCatalog[0]?.id || null);
  const [copied, setCopied] = useState("");

  useEffect(() => {
    const verifyPermission = async () => {
      const profileData = await fetchProfile();
      if (!profileData || !["admin", "ai"].includes(profileData.data.userGroup)) {
        navigate("/");
      }
    };

    verifyPermission();
  }, [navigate]);

  const filteredApis = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    return apiCatalog.filter((api) => {
      const matchesCategory = category === "전체" || api.category === category;
      const matchesMethod = method === "전체" || api.method === method;
      const matchesKeyword = !keyword || [api.title, api.path, api.description, api.category]
        .some((value) => value.toLowerCase().includes(keyword));
      return matchesCategory && matchesMethod && matchesKeyword;
    });
  }, [category, method, search]);

  const copyText = async (key: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(key);
      window.setTimeout(() => setCopied((current) => current === key ? "" : current), 1400);
    } catch {
      setCopied("");
    }
  };

  const getMethodCount = (targetMethod: ApiMethod) => (
    apiCatalog.filter((api) => api.method === targetMethod).length
  );

  return (
    <section id="api-docs">
      <header className="api-docs__header">
        <div>
          <span>DEVELOPER REFERENCE</span>
          <h1>API 명세</h1>
          <p>AI 운영 기능과 기기 연동에 사용하는 API의 요청 방식과 응답 형식을 확인합니다.</p>
        </div>
        <button type="button" onClick={() => copyText("base", BASE_URL)}>
          {copied === "base" ? "복사 완료" : "Base URL 복사"}<span aria-hidden="true">↗</span>
        </button>
      </header>

      <section className="api-docs__base" aria-label="API 기본 정보">
        <div>
          <span>BASE URL</span>
          <code>{BASE_URL}</code>
        </div>
        <p>업데이트 API는 발급받은 <code>key</code> 헤더가 필요하며, 사용자 API는 로그인 토큰을 사용합니다.</p>
      </section>

      <div className="api-docs__summary">
        <article>
          <span>문서화 API</span>
          <strong>{apiCatalog.length}<small>개</small></strong>
        </article>
        <article className="is-get">
          <span>GET</span>
          <strong>{getMethodCount("GET")}<small>개</small></strong>
        </article>
        <article className="is-post">
          <span>POST</span>
          <strong>{getMethodCount("POST")}<small>개</small></strong>
        </article>
        <article>
          <span>카테고리</span>
          <strong>{apiCategories.length}<small>개</small></strong>
        </article>
      </div>

      <div className="api-docs__toolbar">
        <label className="api-docs__search">
          <span>API 검색</span>
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="기능명 또는 /path 검색"
          />
        </label>
        <label>
          <span>카테고리</span>
          <select value={category} onChange={(event) => setCategory(event.target.value)}>
            <option value="전체">전체</option>
            {apiCategories.map((item) => <option value={item} key={item}>{item}</option>)}
          </select>
        </label>
        <label>
          <span>메서드</span>
          <select value={method} onChange={(event) => setMethod(event.target.value as "전체" | ApiMethod)}>
            <option value="전체">전체</option>
            <option value="GET">GET</option>
            <option value="POST">POST</option>
          </select>
        </label>
        <div className="api-docs__result">
          <span>RESULT</span>
          <strong>{filteredApis.length} API</strong>
        </div>
      </div>

      <div className="api-docs__list">
        {filteredApis.map((api) => {
          const isExpanded = expandedId === api.id;
          return (
            <article className={`api-endpoint${isExpanded ? " is-expanded" : ""}`} key={api.id}>
              <div className="api-endpoint__summary">
                <button
                  type="button"
                  className="api-endpoint__toggle"
                  onClick={() => setExpandedId(isExpanded ? null : api.id)}
                  aria-expanded={isExpanded}
                >
                  <span className={`api-method is-${api.method.toLowerCase()}`}>{api.method}</span>
                  <span className="api-endpoint__identity">
                    <strong>{api.title}</strong>
                    <code>{api.path}</code>
                  </span>
                  <span className="api-endpoint__category">{api.category}</span>
                  <span className={`api-auth${api.auth === "없음" ? " is-public" : ""}`}>{api.auth}</span>
                  <i aria-hidden="true">›</i>
                </button>
                <button
                  type="button"
                  className="api-endpoint__copy"
                  onClick={() => copyText(`path-${api.id}`, `${BASE_URL}${api.path}`)}
                >
                  {copied === `path-${api.id}` ? "완료" : "경로 복사"}
                </button>
              </div>

              {isExpanded && (
                <div className="api-endpoint__detail">
                  <div className="api-endpoint__intro">
                    <div>
                      <span>DESCRIPTION</span>
                      <p>{api.description}</p>
                    </div>
                    <dl>
                      <div><dt>인증</dt><dd>{api.auth}</dd></div>
                      <div><dt>Content-Type</dt><dd>{api.contentType}</dd></div>
                    </dl>
                  </div>

                  <section className="api-endpoint__section">
                    <header>
                      <div><span>01</span><strong>요청 파라미터</strong></div>
                      <small>{api.parameters.length} ITEMS</small>
                    </header>
                    {api.parameters.length > 0 ? (
                      <div className="api-parameter-table">
                        <table>
                          <thead>
                            <tr><th>이름</th><th>위치</th><th>타입</th><th>필수</th><th>설명 / 예시</th></tr>
                          </thead>
                          <tbody>
                            {api.parameters.map((parameter) => (
                              <tr key={`${parameter.location}-${parameter.name}`}>
                                <td><code>{parameter.name}</code></td>
                                <td>{parameter.location}</td>
                                <td>{parameter.type}</td>
                                <td><span className={parameter.required ? "is-required" : ""}>{parameter.required ? "필수" : "선택"}</span></td>
                                <td><p>{parameter.description}</p>{parameter.example && <code>{parameter.example}</code>}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="api-endpoint__empty">별도 요청 파라미터가 없습니다.</div>
                    )}
                  </section>

                  {api.requestExample && (
                    <section className="api-endpoint__section">
                      <header>
                        <div><span>02</span><strong>cURL 요청 예시</strong></div>
                        <button type="button" onClick={() => copyText(`curl-${api.id}`, api.requestExample || "")}>
                          {copied === `curl-${api.id}` ? "복사 완료" : "cURL 복사"}
                        </button>
                      </header>
                      <pre><code>{api.requestExample}</code></pre>
                    </section>
                  )}

                  <section className="api-endpoint__section">
                    <header><div><span>03</span><strong>응답 예시</strong></div></header>
                    <pre><code>{api.responseExample}</code></pre>
                  </section>

                  {api.note && <aside className="api-endpoint__note"><strong>NOTE</strong><p>{api.note}</p></aside>}
                </div>
              )}
            </article>
          );
        })}

        {filteredApis.length === 0 && (
          <div className="api-docs__empty">검색 조건에 해당하는 API가 없습니다.</div>
        )}
      </div>
    </section>
  );
};

export default ApiDocs;
